import { useCallback, useMemo, useRef, useState } from 'react';
import { TOCContextState, TOCList, TOCListItem } from '../interfaces/toc';
import { useFirstVisibleElement } from '../../shared/hooks/useFirstVisibleElement';

export function useToc() {
  const [items, setItems] = useState<TOCList>([]);
  const [activeItem, setActiveItem] = useState<TOCContextState['activeItem']>(null);

  const contentContainerRef = useRef<HTMLDivElement>(null);

  const observedElements = useCallback(() => {
    const elements = contentContainerRef.current?.querySelectorAll('.toc-section-title');
    return Array.from(elements ?? []);
  }, []);

  useFirstVisibleElement(observedElements, (element: Element | null) => {
    const id = element?.id ?? undefined;

    if (id !== activeItem?.domId) {
      const _activeItem = items.find(item => item.domId === id);
      setActiveItem(_activeItem ?? null);
    }
  });

  const registerItem = useCallback(
    (item: TOCListItem) => {
      setItems(prevState => [...prevState, item]);
    },
    [items, setItems]
  );

  const jumpToSection = useCallback((item: TOCListItem) => {
    const { domId } = item;

    const element = document.getElementById(domId);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return useMemo<TOCContextState>(() => {
    return {
      items,
      activeItem,
      registerItem,
      setActiveItem,
      jumpToSection,
      contentContainerRef
    };
  }, [items, activeItem, setActiveItem, registerItem, jumpToSection]);
}
